import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  TableContainer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FiCalendar, FiFilter } from "react-icons/fi";
import VmsPageCard from "../components/VmsPageCard";
import { VMS_COLORS } from "../constants/theme";
import { vmsApi } from "../services/vmsApi";
import { isVmsGeneralAdmin } from "../utils/auth";

const STATUS_KEYS = ["REQUESTED", "APPROVED", "DECLINED", "RESCHEDULED", "EXPIRED", "SIGNED_OUT"];

const toIsoIfProvided = (value) => (value ? new Date(value).toISOString() : "");

const prettyDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const VmsDashboard = () => {
  const toast = useToast();
  const isAdmin = isVmsGeneralAdmin();
  const [offices, setOffices] = useState([]);
  const [filters, setFilters] = useState({
    office_id: "",
    start_date: "",
    end_date: "",
  });
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const statusBreakdown = summary?.status_breakdown || {};
  const topOffices = Array.isArray(summary?.top_offices) ? summary.top_offices : [];
  const dashboardMetrics = summary?.dashboard_metrics || {};

  const statusCards = STATUS_KEYS.map((key) => ({
    label: key,
    value: statusBreakdown?.[key] ?? 0,
  }));

  const metricCards = [
    { label: "Active Visitors Now", value: dashboardMetrics?.active_visitors_now ?? 0 },
    { label: "Today Requests", value: dashboardMetrics?.today_total_requests ?? 0 },
    { label: "Today Signed Out", value: dashboardMetrics?.today_signed_out ?? 0 },
    { label: "Pending Count", value: dashboardMetrics?.pending_count ?? 0 },
    { label: "Completed Count", value: dashboardMetrics?.completed_count ?? 0 },
    { label: "Processing Backlog", value: dashboardMetrics?.processing_backlog ?? 0 },
    { label: "Approval Rate", value: `${dashboardMetrics?.approval_rate_percent ?? 0}%` },
    { label: "Decline Rate", value: `${dashboardMetrics?.decline_rate_percent ?? 0}%` },
    { label: "Overstayed Rate", value: `${dashboardMetrics?.overstayed_rate_percent ?? 0}%` },
    { label: "Avg Approval Time", value: `${dashboardMetrics?.avg_approval_time_minutes ?? 0} min` },
    { label: "Peak Hour", value: dashboardMetrics?.peak_hour || "-" },
    { label: "Peak Day", value: dashboardMetrics?.peak_day || "-" },
  ];

  const loadSummary = useCallback(async (nextFilters = filters) => {
    setIsLoading(true);
    try {
      const payload = {
        ...(nextFilters.office_id ? { office_id: nextFilters.office_id } : {}),
        ...(nextFilters.start_date ? { start_date: toIsoIfProvided(nextFilters.start_date) } : {}),
        ...(nextFilters.end_date ? { end_date: toIsoIfProvided(nextFilters.end_date) } : {}),
      };
      const data = await vmsApi.getSummaryReport(payload);
      setSummary(data?.data || data || {});
    } catch (err) {
      toast({
        title: "Failed to load dashboard",
        description: err?.message || "Could not load summary data.",
        status: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => {
    const init = async () => {
      try {
        const officesData = await vmsApi.getOffices();
        setOffices(Array.isArray(officesData) ? officesData : officesData?.data || []);
      } catch {
        setOffices([]);
      }
      await loadSummary();
    };
    init().catch(() => {});
  }, [loadSummary]);

  return (
    <VStack align="stretch" spacing={5}>
      <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="14px" p={4}>
        <Wrap spacing={4} align="end">
            {isAdmin ? (
              <WrapItem minW={{ base: "100%", md: "220px" }}>
                <FormControl>
                  <FormLabel mb={1}>Office</FormLabel>
                  <Select
                    bg="white"
                    value={filters.office_id}
                    onChange={(e) => setFilters((prev) => ({ ...prev, office_id: e.target.value }))}
                  >
                    <option value="">All offices</option>
                    {offices.map((office) => {
                      const id = office.id || office.office_id;
                      return (
                        <option key={id} value={id}>
                          {office.name || office.code || id}
                        </option>
                      );
                    })}
                  </Select>
                </FormControl>
              </WrapItem>
            ) : null}

            <WrapItem minW={{ base: "100%", md: "240px" }}>
              <FormControl>
                <FormLabel mb={1}>Start Date</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FiCalendar} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    bg="white"
                    type="datetime-local"
                    value={filters.start_date}
                    onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))}
                  />
                </InputGroup>
              </FormControl>
            </WrapItem>
            <WrapItem minW={{ base: "100%", md: "240px" }}>
              <FormControl>
                <FormLabel mb={1}>End Date</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FiCalendar} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    bg="white"
                    type="datetime-local"
                    value={filters.end_date}
                    onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))}
                  />
                </InputGroup>
              </FormControl>
            </WrapItem>
            <WrapItem>
              <Button
                leftIcon={<FiFilter />}
                bg={VMS_COLORS.primary}
                color="white"
                onClick={() => loadSummary()}
                isLoading={isLoading}
                _hover={{ bg: VMS_COLORS.primaryDark, transform: "translateY(-1px)" }}
              >
                Apply Filters
              </Button>
            </WrapItem>
        </Wrap>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Stat bg="gray.50" border="1px solid" borderColor="gray.200" p={5} borderRadius="14px">
            <StatLabel>Total Requests</StatLabel>
            <StatNumber fontSize="3xl">{summary?.total_requests ?? 0}</StatNumber>
          </Stat>
          <Stat bg="gray.50" border="1px solid" borderColor="gray.200" p={5} borderRadius="14px">
            <StatLabel>Average Visit Duration</StatLabel>
            <StatNumber fontSize="3xl">{summary?.average_visit_duration_hours ?? 0}h</StatNumber>
          </Stat>
          <Stat bg="gray.50" border="1px solid" borderColor="gray.200" p={5} borderRadius="14px">
            <StatLabel>Report Period</StatLabel>
            <Text fontSize="sm" color="gray.600" mt={1}>
              {prettyDate(summary?.report_period?.start_date)} - {prettyDate(summary?.report_period?.end_date)}
            </Text>
          </Stat>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
        {metricCards.map((item) => (
          <Stat key={item.label} bg="gray.50" border="1px solid" borderColor="gray.200" p={4} borderRadius="12px">
            <StatLabel fontSize="xs" color="gray.600">
              {item.label}
            </StatLabel>
            <StatNumber fontSize="xl">{item.value}</StatNumber>
          </Stat>
        ))}
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
        <VmsPageCard title="Status Breakdown">
          {isLoading && !summary ? <Spinner /> : null}
          <Divider mb={4} />
          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
            {statusCards.map((item) => (
              <Box
                key={item.label}
                bg="gray.50"
                borderRadius="12px"
                p={4}
                border="1px solid"
                borderColor="gray.200"
                _hover={{ borderColor: "green.200", transform: "translateY(-1px)" }}
                transition="all 0.18s ease"
              >
                <Text fontSize="xs" color="gray.500">
                  {item.label}
                </Text>
                <Text fontSize="2xl" fontWeight="700">
                  {item.value}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </VmsPageCard>

        <VmsPageCard title="Top Offices">
          <TableContainer border="1px solid" borderColor="gray.200" borderRadius="12px">
            <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Office</Th>
                <Th isNumeric>Requests</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {topOffices.map((office, idx) => (
                <Tr key={`${office.office_name}-${idx}`}>
                  <Td>{office.office_name || "-"}</Td>
                  <Td isNumeric>{office.count ?? 0}</Td>
                  <Td>
                    <Badge colorScheme="green">Top</Badge>
                  </Td>
                </Tr>
              ))}
              {!topOffices.length ? (
                <Tr>
                  <Td colSpan={3}>
                    <Text color="gray.500" fontSize="sm" py={6} textAlign="center">
                      No office summary data for the selected period.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
          </TableContainer>
        </VmsPageCard>
      </SimpleGrid>
    </VStack>
  );
};

export default VmsDashboard;
